import { getFirebaseAuth, unifiedHttpsCallable } from '@watchlist/shared';
import { isCapacitorNative } from '../utils/platformUtils';
import { nativeFirebase } from './nativeFirebaseService';
import { socialService as _socialService } from '@watchlist/shared';

// Connect to emulator in development - DISABLED for production testing
// if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_USE_FIREBASE_EMULATOR_DISABLED) {
//   try {
//     connectFunctionsEmulator(functions, 'localhost', 5001);
//     console.log('Connected to Functions emulator');
//   } catch (error) {
//     console.warn('Could not connect to Functions emulator:', error);
//   }
// }
console.log('🚀 Using production Firebase Functions');

// Base URL for HTTP functions (adjust based on your Firebase project)
const getFunctionUrl = (functionName: string): string => {
  const projectId = 'watchlists-1c639'; // Use actual project ID
  const region = 'us-central1'; // Default region
  
  // 🚀 TEMPORARILY: Always use production Functions since local emulator doesn't have production data
  // TODO: Switch back to development URLs when emulator is properly seeded with data
  // if (process.env.NODE_ENV === 'development') {
  //   return `http://localhost:5001/${projectId}/${region}/${functionName}`;
  // }
  
  return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
};

// HTTP client with error handling
async function httpRequest(functionName: string, params: Record<string, string> = {}): Promise<any> {
  const url = getFunctionUrl(functionName);
  const queryParams = new URLSearchParams(params);
  const fullUrl = queryParams.toString() ? `${url}?${queryParams}` : url;
  
  try {
    const response = await fetch(fullUrl);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error calling function ${functionName}:`, error);
    throw error;
  }
}

// Movie search interface
export interface MovieSearchResult {
  results: any[];
  page: number;
  total_pages: number;
  total_results: number;
  cached?: boolean;
}

// Movie details interface
export interface MovieDetails {
  id: number;
  title: string;
  overview: string;
  poster_path: string;
  backdrop_path: string;
  release_date: string;
  vote_average: number;
  vote_count: number;
  runtime: number;
  genres: { id: number; name: string }[];
  credits?: {
    cast: any[];
    crew: any[];
  };
  videos?: {
    results: any[];
  };
  images?: {
    backdrops: any[];
    posters: any[];
  };
  cached?: boolean;
}

/**
 * Search for movies using Firebase Functions
 */
export async function searchMovies(
  query: string, 
  page: number = 1, 
  language: string = 'en-US'
): Promise<MovieSearchResult> {
  if (!query.trim()) {
    throw new Error('Search query cannot be empty');
  }
  
  return httpRequest('searchMovies', {
    query: query.trim(),
    page: page.toString(),
    language
  });
}

/**
 * Get movie details using Firebase Functions
 */
/**
 * Extract numeric TMDB ID from various ID formats
 */
function extractTmdbId(movieId: number | string): string {
  if (typeof movieId === 'number') {
    return movieId.toString();
  }
  
  const idStr = movieId.toString();
  
  // Handle tmdb_123456 format
  if (idStr.startsWith('tmdb_')) {
    return idStr.replace('tmdb_', '');
  }
  
  // Handle plain numeric strings
  if (/^\d+$/.test(idStr)) {
    return idStr;
  }
  
  throw new Error(`Invalid movie ID format: ${movieId}`);
}

export async function getMovieDetails(
  movieId: number | string, 
  language: string = 'en-US'
): Promise<MovieDetails> {
  if (!movieId) {
    throw new Error('Movie ID is required');
  }

  // Extract numeric TMDB ID for the Functions API
  const numericId = extractTmdbId(movieId);
  const url = getFunctionUrl(`getMovieDetails/${numericId}`);
  const queryParams = new URLSearchParams({ language });
  const fullUrl = `${url}?${queryParams}`;

  try {
    const response = await fetch(fullUrl);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting movie details:', error);
    throw error;
  }
}

/**
 * Get popular movies using Firebase Functions
 */
export async function getPopularMovies(
  page: number = 1, 
  language: string = 'en-US',
  region: string = 'US'
): Promise<MovieSearchResult> {
  return httpRequest('getPopularMovies', {
    page: page.toString(),
    language,
    region
  });
}

/**
 * Get trending movies using Firebase Functions
 */
export async function getTrendingMovies(
  timeWindow: 'day' | 'week' = 'week',
  page: number = 1
): Promise<MovieSearchResult> {
  return httpRequest('getTrendingMovies', {
    time_window: timeWindow,
    page: page.toString()
  });
}

/**
 * Helper function to get full image URL
 */
export function getImageUrl(path: string, size: string = 'w500'): string {
  if (!path) return '';
  return `https://image.tmdb.org/t/p/${size}${path}`;
}

/**
 * Check if Functions API is available
 */
export async function checkFunctionsHealth(): Promise<boolean> {
  try {
    // Try to get popular movies as a health check
    await getPopularMovies(1);
    return true;
  } catch (error) {
    console.warn('Functions API health check failed:', error);
    return false;
  }
}

/**
 * Feature flag to control Functions vs direct API usage
 */
export const shouldUseFunctionsAPI = (): boolean => {
  // Can be controlled via environment variable
  return process.env.REACT_APP_USE_FUNCTIONS_API !== 'false';
};

/**
 * SOCIAL FEATURES - Using Firebase Functions for atomic operations
 */

// Social operation results
export interface SocialActionResult {
  success: boolean;
  action: string;
  message?: string;
  followersCount?: number;
  followingCount?: number;
  likesCount?: number;
}

export interface FollowStatusResult {
  isFollowing: boolean;
  isFollowedBy: boolean;
  mutualFollow: boolean;
}

export interface LikeStatusResult {
  isUserLiked: boolean;
  likesCount: number;
}



// 🚀 NEW: Central error handling utility
export interface ApiError {
  message: string;
  status: number;
  code?: string;
  retryable?: boolean;
}

export function handleApiError(error: any): ApiError {
  // Rate limiting errors
  if (error.response?.status === 429 || error.code === 'rate-limit') {
    return { 
      message: 'Too many requests. Please wait a moment and try again.', 
      status: 429,
      code: 'RATE_LIMIT',
      retryable: true
    };
  }
  
  // Authentication errors
  if (error.response?.status === 401 || error.code === 'unauthenticated') {
    return { 
      message: 'Please log in to continue.', 
      status: 401,
      code: 'UNAUTHENTICATED',
      retryable: false
    };
  }
  
  // Permission errors
  if (error.response?.status === 403 || error.code === 'permission-denied') {
    return { 
      message: 'You don\'t have permission to perform this action.', 
      status: 403,
      code: 'PERMISSION_DENIED',
      retryable: false
    };
  }
  
  // Network/timeout errors
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('timeout')) {
    return { 
      message: 'Network error. Please check your connection and try again.', 
      status: 500,
      code: 'NETWORK_ERROR',
      retryable: true
    };
  }
  
  // Social operation specific errors
  if (error.message?.includes('cannot follow themselves')) {
    return { 
      message: 'You cannot follow yourself.', 
      status: 400,
      code: 'SELF_FOLLOW',
      retryable: false
    };
  }
  
  if (error.message?.includes('already following')) {
    return { 
      message: 'You are already following this user.', 
      status: 400,
      code: 'ALREADY_FOLLOWING',
      retryable: false
    };
  }
  
  if (error.message?.includes('already liked')) {
    return { 
      message: 'You have already liked this item.', 
      status: 400,
      code: 'ALREADY_LIKED',
      retryable: false
    };
  }
  
  // Server errors
  if (error.response?.status >= 500) {
    return { 
      message: 'Server is temporarily unavailable. Please try again later.', 
      status: error.response.status,
      code: 'SERVER_ERROR',
      retryable: true
    };
  }
  
  // Default error
  return { 
    message: error.message || 'An unexpected error occurred. Please try again.', 
    status: error.response?.status || 500,
    code: 'UNKNOWN_ERROR',
    retryable: true
  };
}

// Enhanced HTTP client with centralized error handling
async function makeAuthenticatedRequest(url: string, options: RequestInit = {}): Promise<any> {
  try {
    let token: string | null = null;
    if (isCapacitorNative()) {
      token = await nativeFirebase.getIdTokenForHttp(false);
    } else {
      const user = getFirebaseAuth().currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }
      token = await user.getIdToken();
    }
    if (!token) {
      throw new Error('User must be authenticated');
    }
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || `HTTP ${response.status}`);
      (error as any).response = response;
      (error as any).data = errorData;
      throw error;
    }

    return await response.json();
  } catch (error) {
    // Apply centralized error handling
    const apiError = handleApiError(error);
    console.error('🚨 API Request failed:', {
      url,
      error: apiError,
      originalError: error
    });
    throw apiError;
  }
}

/**
 * Make authenticated request to Functions
 */
async function authenticatedRequest(
  functionName: string, 
  method: 'GET' | 'POST' = 'GET',
  body?: any,
  params?: Record<string, string>
): Promise<any> {
  try {
    const url = getFunctionUrl(functionName);
    
    let fullUrl = url;
    if (method === 'GET' && params) {
      const queryParams = new URLSearchParams(params);
      fullUrl = `${url}?${queryParams}`;
    }
    
    const options: RequestInit = {
      method,
      body: method === 'POST' && body ? JSON.stringify(body) : undefined
    };
    
    return await makeAuthenticatedRequest(fullUrl, options);
  } catch (error) {
    // Error already handled by makeAuthenticatedRequest with centralized error handling
    console.error(`🚨 ${functionName} failed:`, error);
    throw error;
  }
}

/**
 * Follow or unfollow a user (atomic operation)
 */
export async function followUser(targetUserId: string, action: 'follow' | 'unfollow'): Promise<SocialActionResult> {
  if (!targetUserId) {
    throw new Error('Target user ID is required');
  }
  
  if (!['follow', 'unfollow'].includes(action)) {
    throw new Error('Action must be "follow" or "unfollow"');
  }
  
  return authenticatedRequest('followUser', 'POST', {
    targetUserId,
    action
  });
}

/**
 * Like or unlike a watchlist (atomic operation)
 */
export async function likeWatchlist(watchlistId: string, action: 'like' | 'unlike'): Promise<SocialActionResult> {
  if (!watchlistId) {
    throw new Error('Watchlist ID is required');
  }
  
  if (!['like', 'unlike'].includes(action)) {
    throw new Error('Action must be "like" or "unlike"');
  }
  
  return authenticatedRequest('likeWatchlist', 'POST', {
    watchlistId,
    action
  });
}

/**
 * Get follow status between current user and target user
 */
export async function getFollowStatus(targetUserId: string): Promise<FollowStatusResult> {
  if (!targetUserId) {
    throw new Error('Target user ID is required');
  }
  
  return authenticatedRequest('getFollowStatus', 'GET', undefined, {
    targetUserId
  });
}

/**
 * Get like status for a watchlist
 */
export async function getLikeStatus(watchlistId: string): Promise<LikeStatusResult> {
  if (!watchlistId) {
    throw new Error('Watchlist ID is required');
  }
  
  return authenticatedRequest('getLikeStatus', 'GET', undefined, {
    watchlistId
  });
}

// ─── Follow Request helpers (go direct to Firestore via socialService) ──────

export async function sendFollowRequest(targetUid: string, currentUid: string): Promise<void> {
  return _socialService.sendFollowRequest(currentUid, targetUid);
}

export async function cancelFollowRequest(targetUid: string, currentUid: string): Promise<void> {
  return _socialService.cancelFollowRequest(currentUid, targetUid);
}

export async function acceptFollowRequest(requesterUid: string, _currentUid: string): Promise<void> {
  // Calls the Cloud Function which atomically deletes the request doc and
  // creates the userFollowing relationship via admin SDK (avoids permission issues
  // when the acceptor tries to update the requester's profile stats client-side).
  return authenticatedRequest('acceptFollowRequest', 'POST', { requesterUid });
}

export async function declineFollowRequest(requesterUid: string, currentUid: string): Promise<void> {
  return _socialService.declineFollowRequest(requesterUid, currentUid);
}

export async function hasFollowRequest(currentUid: string, targetUid: string): Promise<boolean> {
  return _socialService.hasFollowRequest(currentUid, targetUid);
}

export async function getPendingFollowRequests(currentUid: string) {
  return _socialService.getPendingFollowRequests(currentUid);
}

/**
 * Utility function to handle optimistic updates with rollback
 */
export async function withOptimisticUpdate<T>(
  optimisticUpdate: () => void,
  apiCall: () => Promise<T>,
  rollback: () => void
): Promise<T> {
  // Apply optimistic update
  optimisticUpdate();
  
  try {
    // Make the API call
    const result = await apiCall();
    return result;
  } catch (error) {
    // Rollback on error
    rollback();
    throw error;
  }
}

/**
 * 🚀 NEW: Retry utility for retryable errors
 */
export async function withRetry<T>(
  apiCall: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: ApiError | undefined;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error: any) {
      lastError = error as ApiError;
      
      // Don't retry if error is not retryable
      if (!lastError.retryable || attempt === maxRetries) {
        throw lastError;
      }
      
      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`🔄 Retrying ${lastError.code} error (attempt ${attempt + 1}/${maxRetries + 1}) after ${delay}ms`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError || new Error('Retry failed');
}

/**
 * 🚀 Enhanced social operations with automatic retry
 */
export async function followUserWithRetry(targetUserId: string, action: 'follow' | 'unfollow'): Promise<SocialActionResult> {
  return withRetry(() => followUser(targetUserId, action));
}

export async function likeWatchlistWithRetry(watchlistId: string, action: 'like' | 'unlike'): Promise<SocialActionResult> {
  return withRetry(() => likeWatchlist(watchlistId, action));
}

/**
 * REVIEW OPERATIONS - Atomic like/unlike for reviews
 */

/**
 * Like or unlike a review (atomic operation)
 */
export async function likeReview(reviewId: string, action: 'like' | 'unlike'): Promise<SocialActionResult> {
  if (!reviewId) {
    throw new Error('Review ID is required');
  }

  if (!['like', 'unlike'].includes(action)) {
    throw new Error('Action must be "like" or "unlike"');
  }

  return authenticatedRequest('likeReview', 'POST', {
    reviewId,
    action
  });
}

/**
 * Get like status for a review
 */
export async function getReviewLikeStatus(reviewId: string): Promise<LikeStatusResult> {
  if (!reviewId) {
    throw new Error('Review ID is required');
  }

  return authenticatedRequest('getReviewLikeStatus', 'GET', undefined, {
    reviewId
  });
}

/**
 * Like review with automatic retry
 */
export async function likeReviewWithRetry(reviewId: string, action: 'like' | 'unlike'): Promise<SocialActionResult> {
  return withRetry(() => likeReview(reviewId, action));
}

/**
 * Like or unlike a crew post (atomic operation)
 */
export async function likeCrewPost(postId: string, action: 'like' | 'unlike'): Promise<SocialActionResult> {
  if (!postId) throw new Error('Post ID is required');
  return authenticatedRequest('likeCrewPost', 'POST', { postId, action });
}

/**
 * Like crew post with automatic retry
 */
export async function likeCrewPostWithRetry(postId: string, action: 'like' | 'unlike'): Promise<SocialActionResult> {
  return withRetry(() => likeCrewPost(postId, action));
}

/**
 * Like or unlike a movie-activity bundle (atomic operation, server lazy-creates the doc).
 * actorUid + kind are validated server-side against the bundleId format.
 */
export async function likeSocialBundle(
  bundleId: string,
  actorUid: string,
  kind: 'movie_watched' | 'movie_added',
  action: 'like' | 'unlike'
): Promise<SocialActionResult> {
  if (!bundleId || !actorUid || !kind) {
    throw new Error('bundleId, actorUid, and kind are required');
  }
  return authenticatedRequest('likeSocialBundle', 'POST', { bundleId, actorUid, kind, action });
}

export async function likeSocialBundleWithRetry(
  bundleId: string,
  actorUid: string,
  kind: 'movie_watched' | 'movie_added',
  action: 'like' | 'unlike'
): Promise<SocialActionResult> {
  return withRetry(() => likeSocialBundle(bundleId, actorUid, kind, action));
}

/**
 * Admin/moderator only: block or unblock a user.
 */
export async function setUserBlocked(targetUid: string, blocked: boolean): Promise<{ success: boolean }> {
  if (!targetUid) throw new Error('targetUid is required');
  return authenticatedRequest('setUserBlocked', 'POST', { targetUid, blocked });
}

/**
 * VISION API - Screenshot processing using Google Cloud Vision
 */

export interface VisionAnalyzeRequest {
  image: string; // base64 encoded image
  features: string[]; // Array of features to detect (e.g., ['TEXT_DETECTION'])
}

export interface VisionAnalyzeResponse {
  textAnnotations?: any[];
  localizedObjectAnnotations?: any[];
  error?: string;
}

/**
 * Analyze image using Google Cloud Vision API via Firebase Functions.
 * Uses unifiedHttpsCallable so it works on iOS/Android Capacitor (raw httpsCallable hangs on native).
 */
export async function analyzeImage(request: VisionAnalyzeRequest): Promise<VisionAnalyzeResponse> {
  try {
    const fn = unifiedHttpsCallable<VisionAnalyzeRequest, VisionAnalyzeResponse>(
      'analyzeImage',
      { timeout: 30_000, region: 'us-central1' }
    );
    const result = await fn(request);
    return result.data;
  } catch (error) {
    console.error('Vision API error:', error);
    const apiError = handleApiError(error);
    throw apiError;
  }
}

/**
 * Extract movie titles from text using Gemini AI.
 * Uses unifiedHttpsCallable so it works on iOS/Android Capacitor.
 */
export async function extractMovieTitles(text: string, maxTitles: number = 50): Promise<string[]> {
  try {
    const fn = unifiedHttpsCallable<
      { text: string; maxTitles: number },
      { success: boolean; titles: string[] }
    >('extractMovieTitles', { timeout: 30_000, region: 'us-central1' });

    const result = await fn({ text, maxTitles });
    const response = result.data;

    if (response.success && response.titles) {
      return response.titles;
    }

    throw new Error('Failed to extract movie titles with Gemini');
  } catch (error) {
    console.error('Movie title extraction error:', error);
    const apiError = handleApiError(error);
    throw apiError;
  }
}

export interface ExtractMoviesFromLinkRow {
  title: string;
  year?: string;
  confidence: number;
}

export interface ExtractMoviesFromLinkResponse {
  success: boolean;
  movies: ExtractMoviesFromLinkRow[];
  source: string;
  totalFound: number;
}

/**
 * Import movie titles from a public URL (IMDb/Letterboxd lists, Instagram/Pinterest posts with carousel OCR, etc.).
 * Uses unifiedHttpsCallable for iOS/Android compatibility.
 */
export async function extractMoviesFromLink(
  url: string,
  maxMovies: number = 100
): Promise<ExtractMoviesFromLinkResponse> {
  const fn = unifiedHttpsCallable<
    { url: string; maxMovies?: number },
    ExtractMoviesFromLinkResponse & { extractedAt?: string }
  >('extractMovieLinks', {
    timeout: 120000,
    region: 'us-central1',
  });

  try {
    const { data } = await fn({ url: url.trim(), maxMovies });
    if (!data?.success || !Array.isArray(data.movies)) {
      throw new Error('Invalid response from link import');
    }
    return {
      success: data.success,
      movies: data.movies,
      source: data.source,
      totalFound: data.totalFound,
    };
  } catch (error) {
    console.error('extractMoviesFromLink error:', error);
    throw handleApiError(error);
  }
}

export interface EnsureReviewShareImageResult {
  url: string;
  cached: boolean;
}

/**
 * Ensures a 1200×1600 share JPEG exists for the review (callable).
 * Caller must be signed in; may generate for own reviews (including only-me) or any public review.
 * Idempotent: returns existing URL unless force is true.
 */
export async function ensureReviewShareImage(
  reviewId: string,
  options?: { force?: boolean }
): Promise<EnsureReviewShareImageResult> {
  try {
    // CRITICAL: Use unifiedHttpsCallable for cross-platform compatibility
    // - On iOS/Android: Uses native @capacitor-firebase/functions plugin
    // - On Web: Uses Firebase web SDK
    // Set 120s timeout to match Cloud Function timeout (image generation takes 25-60s)
    const fn = unifiedHttpsCallable<
      { type: 'review'; reviewId: string; force: boolean },
      { url: string; cached: boolean }
    >('generateShareImage', {
      timeout: 120000, // 120 seconds
    });

    const result = await fn({
      type: 'review',
      reviewId,
      force: Boolean(options?.force),
    });

    const data = result.data;
    if (!data?.url || typeof data.url !== 'string') {
      throw new Error('generateShareImage: invalid response');
    }
    return { url: data.url, cached: Boolean(data.cached) };
  } catch (error) {
    console.error('ensureReviewShareImage error:', error);
    throw handleApiError(error);
  }
}

export async function generateWatchlistShareImage(watchlistId: string, options?: { force?: boolean }): Promise<{ url: string; cached: boolean }> {
  type WatchlistImageResponse = { url?: string; cached?: boolean; imageBase64?: string; mimeType?: string };
  const fn = unifiedHttpsCallable<{ type: 'watchlist'; watchlistId: string; force?: boolean }, WatchlistImageResponse>('generateShareImage', { timeout: 120000 });
  const result = await fn({ type: 'watchlist', watchlistId, force: options?.force });
  if (result.data?.url) return { url: result.data.url, cached: result.data.cached ?? false };
  // Backward compat: old CF returned base64 before Storage upload was added
  if (result.data?.imageBase64) {
    const mime = result.data.mimeType ?? 'image/jpeg';
    return { url: `data:${mime};base64,${result.data.imageBase64}`, cached: false };
  }
  throw new Error('generateWatchlistShareImage: invalid response');
}

export async function generateMovieShareImage(movieId: string, options?: { force?: boolean; withRating?: boolean; rating?: number }): Promise<{ url: string; cached: boolean }> {
  type MovieImageResponse = { url?: string; cached?: boolean };
  const fn = unifiedHttpsCallable<{ type: 'movie'; movieId: string; force?: boolean; withRating?: boolean; rating?: number }, MovieImageResponse>('generateShareImage', { timeout: 120000 });
  const result = await fn({ type: 'movie', movieId, force: options?.force, withRating: options?.withRating, rating: options?.rating });
  if (result.data?.url) return { url: result.data.url, cached: result.data.cached ?? false };
  throw new Error('generateMovieShareImage: invalid response');
}

// All types are already exported as interfaces above
// No need for duplicate exports

export interface DirectorShareItem {
  name: string;
  profilePath: string | null;
  movieCount: number;
  averageRating: number;
  scoreLabel: string;
  topMovies: Array<{ posterPath: string | null; title: string }>;
}

export async function generateTopDirectorsShareImage(
  directors: DirectorShareItem[],
  sortLabel: string,
): Promise<{ url: string }> {
  const fn = unifiedHttpsCallable<
    { type: 'topDirectors'; directors: DirectorShareItem[]; sortLabel: string },
    { url: string; cached: boolean }
  >('generateShareImage', { timeout: 120000 });
  const result = await fn({ type: 'topDirectors', directors, sortLabel });
  if (!result.data?.url) throw new Error('generateTopDirectorsShareImage: invalid response');
  return { url: result.data.url };
}